import { Skeleton } from "antd"
import { useTranslation } from "react-i18next"
import InfiniteScroll from "react-infinite-scroll-component"

import { surfaceColor, surfaceText } from "../../../constants/surface-tokens"
import {
  NOTIFICATION_SECTION,
  type Notification,
  type NotificationSection
} from "../types/notification.types.ts"
import { NotificationsListCard } from "./NotificationsListCard.tsx"

interface NotificationsListProps {
  notifications: Notification[]
  onMarkAsRead: (id: number) => void
  onCardClick: (notification: Notification) => void
  onArchive: (id: number) => void
  hasMore: boolean
  onLoadMore: () => void
  height: string | number
  density?: "compact" | "comfortable"
  showSecondaryActions?: boolean
  scrollableTarget?: string
}

const SECTION_ORDER: NotificationSection[] = [
  NOTIFICATION_SECTION.NEEDS_ATTENTION,
  NOTIFICATION_SECTION.RECENT_UPDATES
]

const SECTION_LABEL: Record<NotificationSection, string> = {
  [NOTIFICATION_SECTION.NEEDS_ATTENTION]:
    "notifications.sections.needsAttention",
  [NOTIFICATION_SECTION.RECENT_UPDATES]: "notifications.sections.recentUpdates"
}

const sentAt = (notification: Notification) =>
  Date.parse(notification?.timestamp) || 0

const levelOf = (notification: Notification) =>
  typeof notification?.level === "number" ? notification.level : 0

const byPosition =
  (section: NotificationSection) => (a: Notification, b: Notification) => {
    // A notification already read falls back into plain time order, so level
    // must not rank the other section.
    if (section === NOTIFICATION_SECTION.NEEDS_ATTENTION) {
      const byLevel = levelOf(b) - levelOf(a)
      if (byLevel !== 0) return byLevel
    }
    // Date keeps milliseconds while the server's timestamps carry microseconds,
    // so a burst of arrivals ties here even where the rows differ.
    return sentAt(b) - sentAt(a) || (b?.id ?? 0) - (a?.id ?? 0)
  }

export function NotificationsList({
  notifications,
  onMarkAsRead,
  onCardClick,
  onArchive,
  hasMore,
  onLoadMore,
  height,
  density = "comfortable",
  showSecondaryActions = true,
  scrollableTarget
}: NotificationsListProps) {
  const { t } = useTranslation()

  // An older backend labels nothing, and dropping those rows would be worse
  // than filing them in the wider section.
  const bySection = SECTION_ORDER.map((section) => ({
    section,
    rows: (notifications ?? [])
      .filter(
        (notification) =>
          (notification?.section ?? NOTIFICATION_SECTION.RECENT_UPDATES) ===
          section
      )
      // A row read in place changes section without moving in the loaded list.
      .sort(byPosition(section))
  })).filter(({ rows }) => rows.length > 0)

  return (
    <InfiniteScroll
      dataLength={notifications?.length}
      loader={
        <div style={{ padding: 16 }}>
          <Skeleton paragraph={{ rows: 1 }} active />
        </div>
      }
      next={onLoadMore}
      hasMore={hasMore}
      className="tailwind-scroll"
      style={{ overflowX: "hidden" }}
      endMessage={null}
      height={scrollableTarget ? undefined : height}
      scrollableTarget={scrollableTarget}
    >
      {bySection.map(({ section, rows }) => (
        <section key={section} data-testid={`section-${section}`}>
          <header
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 16px 8px",
              background: surfaceColor.canvas,
              borderBottom: `1px solid ${surfaceColor.border}`
            }}
          >
            <h2
              style={{
                ...surfaceText.chip,
                margin: 0,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: surfaceColor.chipFg
              }}
            >
              {t(SECTION_LABEL[section])}
            </h2>
            <span
              data-testid="section-count"
              style={{ ...surfaceText.chip, color: surfaceColor.inkMuted }}
            >
              {rows.reduce((n, row) => n + (row?.group_count ?? 1), 0)}
            </span>
          </header>
          {rows.map((notification) => (
            <NotificationsListCard
              key={notification?.id}
              notification={notification}
              onMarkAsRead={onMarkAsRead}
              onCardClick={onCardClick}
              onArchive={onArchive}
              density={density}
              showSecondaryActions={showSecondaryActions}
            />
          ))}
        </section>
      ))}
    </InfiniteScroll>
  )
}
